import { ComponentShowcase } from './ComponentShowcase';
import { Button } from '@/ui/base/button';
import { Badge } from '@/ui/base/badge';
import { Input } from '@/ui/base/input';
import { Checkbox } from '@/ui/base/checkbox';
import { Switch } from '@/ui/base/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/base/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { RadioGroup, RadioGroupItem } from '@/ui/base/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/ui/base/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/ui/base/accordion';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/base/popover';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/ui/base/sheet';
import { Separator } from '@/ui/base/separator';
import { Progress } from '@/ui/base/progress';
import { ScrollArea } from '@/ui/base/scroll-area';
import { Loader2, Plus, Trash2, Download, HelpCircle, Info } from 'lucide-react';
import { Label } from '@/ui/base/label';

import { GradeDePrimitivos } from './amostras/Primitivos';

export function ComponentesBaseTab() {
  return (
    <div className="space-y-6">
      {/* Button */}
      <ComponentShowcase
        id="button"
        title="Button"
        description="6 variantes × 4 tamanhos — todas aceitam ícone e estado de carregamento"
        code={`<Button>Primário</Button>
<Button variant="destructive">Excluir</Button>
<Button variant="outline">Outline</Button>
<Button variant="secondary">Secundário</Button>
<Button variant="ghost">Ghost</Button>
<Button variant="link">Link</Button>
<Button size="sm">Pequeno</Button>
<Button size="lg">Grande</Button>
<Button size="icon" aria-label="Adicionar"><Plus /></Button>
<Button disabled><Loader2 className="animate-spin" /> Salvando...</Button>`}
        propsTable={[
          [
            'variant',
            '"default" | "destructive" | "outline" | "secondary" | "ghost" | "link"',
            '"default"',
            'Estilo visual do botão',
          ],
          ['size', '"default" | "sm" | "lg" | "icon"', '"default"', 'Tamanho do botão'],
          ['asChild', 'boolean', 'false', 'Renderizar como slot (compose com links)'],
          ['disabled', 'boolean', 'false', 'Desabilita interação e aplica opacidade'],
        ]}
      >
        <div className="flex flex-col gap-4 w-full">
          <div className="flex flex-wrap items-center gap-2">
            <Button>Primário</Button>
            <Button variant="destructive">
              <Trash2 /> Excluir
            </Button>
            <Button variant="outline">Outline</Button>
            <Button variant="secondary">Secundário</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="link">Link</Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm">Pequeno</Button>
            <Button size="default">Padrão</Button>
            <Button size="lg">Grande</Button>
            <Button size="icon" aria-label="Adicionar">
              <Plus />
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button disabled>
              <Loader2 className="animate-spin" /> Salvando...
            </Button>
            <Button variant="outline" disabled>
              Desabilitado
            </Button>
            <Button>
              <Download /> Exportar CSV
            </Button>
          </div>
        </div>
      </ComponentShowcase>

      {/* Badge */}
      <ComponentShowcase
        id="badge"
        title="Badge"
        description="4 variantes — usadas em situação, contadores e etiquetas"
        code={`<Badge>Default</Badge>
<Badge variant="secondary">Secundário</Badge>
<Badge variant="destructive">Crítico</Badge>
<Badge variant="outline">Outline</Badge>`}
        propsTable={[
          [
            'variant',
            '"default" | "secondary" | "destructive" | "outline"',
            '"default"',
            'Estilo visual',
          ],
        ]}
      >
        <Badge>Default</Badge>
        <Badge variant="secondary">Secundário</Badge>
        <Badge variant="destructive">Crítico</Badge>
        <Badge variant="outline">Outline</Badge>
      </ComponentShowcase>

      {/* Input */}
      <ComponentShowcase
        id="input"
        title="Input"
        description="Campo de texto — h-11, rounded-xl, com anel de foco no Azul Sinalização"
        code={`<Input placeholder="Nome do credenciado" />
<Input type="email" placeholder="email@exemplo.com" />
<Input disabled placeholder="Desabilitado" />`}
        propsTable={[
          ['type', 'string', '"text"', 'Tipo HTML do input'],
          ['placeholder', 'string', '-', 'Texto de exemplo dentro do campo'],
          ['disabled', 'boolean', 'false', 'Desabilita o campo'],
        ]}
      >
        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <Input placeholder="Nome do credenciado" className="max-w-xs" />
          <Input type="email" placeholder="email@exemplo.com" className="max-w-xs" />
          <Input disabled placeholder="Desabilitado" className="max-w-xs" />
        </div>
      </ComponentShowcase>

      {/* Select */}
      <ComponentShowcase
        id="select"
        title="Select"
        description="Lista suspensa de seleção única, com texto de exemplo, grupos e itens desabilitados"
        code={`<Select>
  <SelectTrigger className="w-[200px]">
    <SelectValue placeholder="Selecione a DRE" />
  </SelectTrigger>
  <SelectContent>
    <SelectItem value="cl">Campo Limpo</SelectItem>
    <SelectItem value="bt">Butantã</SelectItem>
    <SelectItem value="ip">Ipiranga</SelectItem>
  </SelectContent>
</Select>`}
      >
        <div className="flex flex-wrap gap-3">
          <Select>
            <SelectTrigger className="w-[200px]" aria-label="DRE">
              <SelectValue placeholder="Selecione a DRE" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cl">Campo Limpo</SelectItem>
              <SelectItem value="bt">Butantã</SelectItem>
              <SelectItem value="ip">Ipiranga</SelectItem>
              <SelectItem value="sm">São Mateus</SelectItem>
            </SelectContent>
          </Select>
          <Select defaultValue="ativo">
            <SelectTrigger className="w-[160px]" aria-label="Situação">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ativo">Ativo</SelectItem>
              <SelectItem value="inativo">Inativo</SelectItem>
              <SelectItem value="bloqueado">Bloqueado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </ComponentShowcase>

      {/* RadioGroup */}
      <ComponentShowcase
        id="radio-group"
        title="RadioGroup"
        description="Grupo de opções de seleção única — usado em formulários para escolhas mutuamente exclusivas"
        code={`<RadioGroup defaultValue="pf">
  <div className="flex items-center space-x-2">
    <RadioGroupItem value="pf" id="pf" />
    <Label htmlFor="pf">Pessoa Física</Label>
  </div>
  <div className="flex items-center space-x-2">
    <RadioGroupItem value="pj" id="pj" />
    <Label htmlFor="pj">Pessoa Jurídica</Label>
  </div>
</RadioGroup>`}
      >
        <RadioGroup defaultValue="pf" className="flex flex-col gap-3">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="pf" id="ds-pf" />
            <Label htmlFor="ds-pf" className="text-sm">
              Pessoa Física (CPF)
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="pj" id="ds-pj" />
            <Label htmlFor="ds-pj" className="text-sm">
              Pessoa Jurídica (CNPJ)
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="mei" id="ds-mei" />
            <Label htmlFor="ds-mei" className="text-sm">
              MEI
            </Label>
          </div>
        </RadioGroup>
      </ComponentShowcase>

      {/* Checkbox & Switch */}
      <ComponentShowcase
        id="checkbox-switch"
        title="Checkbox e Switch"
        description="Controles de formulário para opções binárias"
        code={`<div className="flex items-center gap-2">
  <Checkbox id="terms" />
  <Label htmlFor="terms">Aceito os termos</Label>
</div>

<div className="flex items-center gap-2">
  <Switch id="active" />
  <Label htmlFor="active">Ativo</Label>
</div>`}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Checkbox id="ds-terms" />
            <Label htmlFor="ds-terms" className="text-sm">
              Aceito os termos de credenciamento
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="ds-check2" defaultChecked />
            <Label htmlFor="ds-check2" className="text-sm">
              Curso de transporte escolar (concluído)
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="ds-switch" />
            <Label htmlFor="ds-switch" className="text-sm">
              Credenciado ativo
            </Label>
          </div>
        </div>
      </ComponentShowcase>

      {/* Dialog */}
      <ComponentShowcase
        id="dialog"
        title="Dialog"
        description="Janela sobreposta ao conteúdo — para formulário curto, confirmação e detalhe"
        code={`<Dialog>
  <DialogTrigger asChild>
    <Button variant="outline">Abrir Dialog</Button>
  </DialogTrigger>
  <DialogContent>
    <DialogHeader>
      <DialogTitle>Título do Dialog</DialogTitle>
      <DialogDescription>Descrição auxiliar</DialogDescription>
    </DialogHeader>
    {/* conteúdo */}
    <DialogFooter>
      <Button>Confirmar</Button>
    </DialogFooter>
  </DialogContent>
</Dialog>`}
      >
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline">Abrir Dialog</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Operação</DialogTitle>
              <DialogDescription>
                Deseja prosseguir com a emissão da ordem de serviço nº 1234?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline">Cancelar</Button>
              <Button>Confirmar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </ComponentShowcase>

      {/* Card */}
      <ComponentShowcase
        id="card"
        title="Card"
        description="Contêiner com borda, sombra e cantos arredondados — base para seções, métricas e agrupamentos"
        code={`<Card>
  <CardHeader>
    <CardTitle>Título</CardTitle>
    <CardDescription>Descrição</CardDescription>
  </CardHeader>
  <CardContent>Conteúdo</CardContent>
</Card>`}
      >
        <div className="grid sm:grid-cols-2 gap-4 w-full">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Credenciados Ativos</CardTitle>
              <CardDescription>Total por DRE</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">127</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Veículos Vistoriados</CardTitle>
              <CardDescription>Mês corrente</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">342</p>
            </CardContent>
          </Card>
        </div>
      </ComponentShowcase>

      {/* Tabs */}
      <ComponentShowcase
        id="tabs"
        title="Tabs"
        description="Abas de navegação para organizar conteúdo em seções — aba ativa na cor primária e rolagem horizontal quando não cabem"
        code={`<Tabs defaultValue="geral">
  <TabsList>
    <TabsTrigger value="geral">Geral</TabsTrigger>
    <TabsTrigger value="docs">Documentos</TabsTrigger>
  </TabsList>
  <TabsContent value="geral">...</TabsContent>
  <TabsContent value="docs">...</TabsContent>
</Tabs>`}
      >
        <Tabs defaultValue="geral" className="w-full">
          <TabsList>
            <TabsTrigger value="geral">Geral</TabsTrigger>
            <TabsTrigger value="documentos">Documentos</TabsTrigger>
            <TabsTrigger value="historico">Histórico</TabsTrigger>
          </TabsList>
          <TabsContent value="geral" className="mt-3">
            <p className="text-sm text-muted-foreground">Conteúdo da aba Geral</p>
          </TabsContent>
          <TabsContent value="documentos" className="mt-3">
            <p className="text-sm text-muted-foreground">Conteúdo da aba Documentos</p>
          </TabsContent>
          <TabsContent value="historico" className="mt-3">
            <p className="text-sm text-muted-foreground">Conteúdo da aba Histórico</p>
          </TabsContent>
        </Tabs>
      </ComponentShowcase>

      {/* Accordion */}
      <ComponentShowcase
        id="accordion"
        title="Accordion"
        description="Seções recolhíveis para conteúdo extenso — uma ou várias abertas (single ou multiple)"
        code={`<Accordion type="single" collapsible>
  <AccordionItem value="item-1">
    <AccordionTrigger>Seção 1</AccordionTrigger>
    <AccordionContent>Conteúdo</AccordionContent>
  </AccordionItem>
</Accordion>`}
      >
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="req">
            <AccordionTrigger>Requisitos para Credenciamento</AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground">
              CNH categoria D ou superior, curso de transporte escolar, vistoria aprovada, certidão
              criminal negativa.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="docs">
            <AccordionTrigger>Documentos Necessários</AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground">
              RG, CPF, CNH, CRLV, comprovante de residência, CRMC, certidão criminal.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </ComponentShowcase>

      {/* Popover */}
      <ComponentShowcase
        id="popover"
        title="Popover"
        description="Balão flutuante preso ao botão que o abre — para filtro rápido, seletor de data e detalhe curto"
        code={`<Popover>
  <PopoverTrigger asChild>
    <Button variant="outline">Abrir</Button>
  </PopoverTrigger>
  <PopoverContent>Conteúdo</PopoverContent>
</Popover>`}
      >
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              <Info className="h-3.5 w-3.5 mr-1.5" /> Detalhes
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <p className="text-sm font-semibold mb-1">Credenciado #127</p>
            <p className="text-xs text-muted-foreground">Pessoa Exemplo Um — DRE Campo Limpo</p>
            <p className="text-xs text-muted-foreground mt-1">Status: Ativo desde 15/03/2024</p>
          </PopoverContent>
        </Popover>
      </ComponentShowcase>

      {/* Sheet */}
      <ComponentShowcase
        id="sheet"
        title="Sheet"
        description="Painel lateral deslizante (gaveta) — para notificações, detalhes e formulários curtos. Abre de qualquer lado."
        code={`<Sheet>
  <SheetTrigger asChild>
    <Button>Abrir Sheet</Button>
  </SheetTrigger>
  <SheetContent>
    <SheetHeader>
      <SheetTitle>Título</SheetTitle>
      <SheetDescription>Descrição</SheetDescription>
    </SheetHeader>
  </SheetContent>
</Sheet>`}
      >
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline">Abrir Sheet</Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Notificações</SheetTitle>
              <SheetDescription>Últimas atualizações do sistema</SheetDescription>
            </SheetHeader>
            <div className="mt-4 space-y-3">
              {[
                'Termo #89 aprovado',
                'Vistoria agendada — Placa ABC-1D23',
                'Novo credenciado cadastrado',
              ].map((msg) => (
                <div key={msg} className="p-3 rounded-lg border border-border bg-muted/30 text-sm">
                  {msg}
                </div>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </ComponentShowcase>

      {/* Separator */}
      <ComponentShowcase
        id="separator"
        title="Separator"
        description="Divisor horizontal ou vertical para separar seções de conteúdo"
        code={`<Separator />
<Separator orientation="vertical" className="h-6" />`}
      >
        <div className="w-full space-y-3">
          <p className="text-sm">Seção A</p>
          <Separator />
          <p className="text-sm">Seção B</p>
          <div className="flex items-center gap-3 h-6">
            <span className="text-sm">Item 1</span>
            <Separator orientation="vertical" />
            <span className="text-sm">Item 2</span>
            <Separator orientation="vertical" />
            <span className="text-sm">Item 3</span>
          </div>
        </div>
      </ComponentShowcase>

      {/* Progress */}
      <ComponentShowcase
        id="progress"
        title="Progress"
        description="Barra de progresso horizontal — usada em assistentes de etapas, envio de arquivo e indicadores de completude"
        code={`<Progress value={65} className="h-2" />`}
      >
        <div className="w-full space-y-4">
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Documentos enviados</span>
              <span>4 de 7</span>
            </div>
            <Progress value={57} className="h-2" aria-label="Documentos enviados" />
          </div>
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Inscrição completa</span>
              <span>100%</span>
            </div>
            <Progress value={100} className="h-2" aria-label="Inscrição completa" />
          </div>
        </div>
      </ComponentShowcase>

      {/* ScrollArea */}
      <ComponentShowcase
        id="scroll-area"
        title="ScrollArea"
        description="Contêiner com rolagem própria — barras de rolagem finas no lugar das nativas"
        code={`<ScrollArea className="h-48 rounded-md border p-4">
  {/* conteúdo longo */}
</ScrollArea>`}
      >
        <ScrollArea className="h-40 w-full rounded-xl border border-border p-4">
          <div
            className="space-y-2 rounded-md focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- rolagem que só o mouse alcança é barreira para o teclado (WCAG 2.1.1)
            tabIndex={0}
            role="region"
            aria-label="Lista com rolagem"
          >
            {Array.from({ length: 15 }).map((_, i) => (
              <p key={i} className="text-sm">
                Credenciado #{i + 1} — DRE{' '}
                {['Campo Limpo', 'Butantã', 'Ipiranga', 'São Mateus', 'Penha'][i % 5]}
              </p>
            ))}
          </div>
        </ScrollArea>
      </ComponentShowcase>

      {/* Tooltip */}
      <ComponentShowcase
        id="tooltip"
        title="Tooltip"
        description="Dica ao passar o mouse ou focar pelo teclado — atraso padrão de 200 ms"
        code={`<Tooltip>
  <TooltipTrigger asChild>
    <Button variant="ghost" size="icon" aria-label="Ajuda"><HelpCircle /></Button>
  </TooltipTrigger>
  <TooltipContent>Informações sobre o campo</TooltipContent>
</Tooltip>`}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Ajuda">
              <HelpCircle className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>CPF ou CNPJ do credenciado</TooltipContent>
        </Tooltip>
        <span className="text-xs text-muted-foreground">← Passe o mouse</span>
      </ComponentShowcase>

      <GradeDePrimitivos />
    </div>
  );
}
